import * as React from 'react';
import styles from "./styles.module.css";
import { useLanguageLocale } from '../../../hooks/language-locale-hooks';
import { MaintenanceDetailsList } from '../../../types/maintenance';
import { CURRENT_PAGE_SESSION, RECORD_PER_PAGE_LIMIT } from '../../../constants';
import { useDispatch, useSelector } from 'react-redux';
import { Selectors } from '../../../selectors';
import { UserManagementActions } from '../../../actions/actions/usermanagement-actions';
import { Actions, Thunks } from '../../../actions';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import { getScheduledDaysFromDate } from '../../enum/DateTimeFormat';
import { AlarmBadgeStatus } from '../../../components/AlarmBadge/AlarmBadge';
import { getServiceDateStatusState, getServiceType, ticketStatus as maintenanceTicketStatus } from '../../enum/ENUM';
import moment from 'moment';
import { CalendarMonth, ChevronRight, Star, StarBorder } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { pushAppRoute } from '../../../data/domain/route-manager';
import { useHistory } from 'react-router';

export interface Props {
    organizationId?: string;
    customerId?: string;
    systemId?: string;
    serviceType?: number;
    ticketStatus?: number;
    searchText?: string;
    isFavorite?: boolean;
    currentPage: number;
    setCurrentPage: (current: number) => void;
}

const MaintenanceListTable: React.FunctionComponent<Props> = (Props) => {

    const {
        organizationId,
        customerId,
        systemId,
        serviceType,
        ticketStatus,
        searchText,
        isFavorite,
        currentPage,
        setCurrentPage
    } = Props;

    const { t } = useLanguageLocale();
    const user = useSelector(Selectors.Usermanagement.usermanagement);
    const dispatch = useDispatch();
    const history = useHistory();
    const [maintenanceActivityList, setMaintenanceActivityList] = React.useState<MaintenanceDetailsList>([]);
    const [isDataLoaded, setIsDataLoaded] = React.useState(true);
    const [isFirstCall, setFirstCall] = React.useState(true);
    const [totalRecords, setTotalRecords] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(RECORD_PER_PAGE_LIMIT);
    const [offset, setOffset] = React.useState(1);
    const [columns, setColumns] = React.useState<Column[]>([
        {
            id: 'isFavorite', label: '', hideSortIcon: true, format(value, record) {
                return record.isFavorite == 'true' ?
                    <Star data-testid='removeMarkAsFaVBuilding' onClick={() => { }} style={{ color: '#007BC1' }} /> :
                    <StarBorder data-testid='MarkAsFaVBuilding' onClick={() => { }} style={{ color: '#727E84' }} />
            },
        },
        { id: 'ticketNumber', sortColumn: 'ticketNumber', sortDirection: 'asc', label: t('MAINTENANCE_DETAILS.TICKET_NO') },
        {
            id: 'organization', label: t('DEVICE_DETAILS.ORGANIZATION'), format: (value, row) => {
                return row?.organizationName;
            },
        },
        { id: 'customerName', label: t('DASHBORAD.CUSTOMERS'), sortColumn: 'customerName', sortDirection: 'asc', },
        { id: 'sLANumber', label: t('USERS.SLA_MANDATORY_HEADER'), sortColumn: 'sLANumber', sortDirection: 'asc', },
        { id: 'systemName', label: t('DEVICE_DETAILS.SYSTEMS'), sortColumn: 'systemName', sortDirection: 'asc', },
        { id: 'technicianName', label: t('DEVICE_DETAILS.TECHNICIAN'), sortColumn: 'technicianName', sortDirection: 'asc', },
        {
            id: 'serviceDate', sortColumn: 'serviceDate', sortDirection: 'asc', width: '15%', label: <span style={{ display: 'inline-flex', alignItems: 'center', columnGap: '5px' }}>
                {t('MAINTENANCE_DETAILS.SERVICE_DATE')} <CalendarMonth style={{ color: '#727E84' }} />
            </span>, format(value, row) {
                const ticketStatusNumber = row.ticketStatus !== 0 ? row.ticketStatus - 1 : row.ticketStatus;
                const ticketStatusName = maintenanceTicketStatus[ticketStatusNumber].name !== null ? maintenanceTicketStatus[ticketStatusNumber].name : 'MAINTENANCE_DETAILS.STATUS_SCHEDULED';
                const ticketStatusState = getServiceDateStatusState(ticketStatusName).statusTitle === 'MAINTENANCE_DETAILS.STATUS_SCHEDULED' ? true : false;

                return <span style={{ display: 'inline-flex', columnGap: '5px', alignItems: 'center' }}>
                    {moment(row.serviceDate).format('DD MMM YYYY')}
                    <AlarmBadgeStatus
                        statusName={ticketStatusState ? t(getScheduledDaysFromDate(row.serviceDate, t)) : t(getServiceDateStatusState(ticketStatusName).statusTitle)}
                        statusColor={ticketStatusState ? getServiceDateStatusState(getScheduledDaysFromDate(row.serviceDate, t)).statusColor : getServiceDateStatusState(ticketStatusName).statusColor}
                        foregroundColor={ticketStatusState ? getServiceDateStatusState(getScheduledDaysFromDate(row.serviceDate, t)).foregroundColor : getServiceDateStatusState(ticketStatusName).foregroundColor} alarmStatus={''} />
                </span>
            },
        },
        {
            id: 'serviceType', label: t('MAINTENANCE_DETAILS.SERVICE_TYPE'), sortColumn: 'serviceType', sortDirection: 'asc', format(value, row) {
                return t(getServiceType(row.serviceType))
            },
        },
        {
            id: 'action', label: '', format(value, row) {
                return <>
                    <IconButton edge="end" id="list-item-next-icon" onClick={() => {
                        navigateToMaintenanceDetail(row);
                    }}>
                        <ChevronRight />
                    </IconButton>
                </>
            },
            hideSortIcon: true
        },
    ]);

    const navigateToMaintenanceDetail = (row: any) => {
        pushAppRoute(history,
            { type: 'MaintenanceDetails', maintenanceId: row.id },
            { backRoute: 'Maintenance', maintenanceId: row.id, sourcePath: 'Maintenance' });
    }

    const getMaintenanceList = (list: MaintenanceDetailsList): MaintenanceDetailsList => {
        const records = list.map((data, maintenanceActivity) => {
            return (
                {
                    ...data,
                    isFavorite: data.isFavorite === 'true' ? 'true' : 'false',
                }
            );
        });

        return records;
    };

    const handlePageChange = (pageNumber: number) => {
        window.sessionStorage.setItem(CURRENT_PAGE_SESSION, pageNumber.toString());
        setOffset(pageNumber + 1);
        setCurrentPage(pageNumber);
    }

    const handleSort = (columnKey: string, direction: string) => {
        const isAsc = direction === 'asc';
        const sortedData = maintenanceActivityList.sort((a, b) => {
            if (isAsc) {
                // @ts-ignore
                return a[columnKey] > b[columnKey] ? 1 : -1;
            }
            // @ts-ignore
            return a[columnKey] < b[columnKey] ? 1 : -1;
        });

        setColumns((prevColumns) =>
            prevColumns.map((column) =>
                column.sortColumn === columnKey
                    ? { ...column, sortDirection: column.sortDirection === 'asc' ? 'desc' : 'asc' }
                    : column
            )
        );

        setMaintenanceActivityList([...sortedData]);
    }

    const handleRowsPerPageChange = (rowsPerPage: number) => {
        setRowsPerPage(rowsPerPage);
    }

    React.useEffect(() => {
        dispatch(Actions.UserManagement.maintenanceDetailUnmounted())
        dispatch(Thunks.UserManagement.getMaintenanceDetail(organizationId, '', '', searchText, customerId, '', systemId, '', offset.toString(), rowsPerPage.toString(), '', '', '', ticketStatus, serviceType));
        setFirstCall(false);
    }, []);// eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (!isFirstCall) {
            dispatch(Thunks.UserManagement.getMaintenanceDetail(organizationId, '', '', searchText, customerId, '', systemId, '', offset.toString(), rowsPerPage.toString(), '', '', '', ticketStatus, serviceType));
        }
    }, [organizationId, customerId, systemId, serviceType, ticketStatus, searchText, rowsPerPage, offset]);// eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (user.maintenanceDetailLoading) {
            setIsDataLoaded(true);
        }
        else {
            if (!user.maintenanceDetailLoading && user.maintenanceDetailSuccess) {
                if (user.maintenanceDetail.detail.length > 0) {
                    const MaintenanceList = getMaintenanceList(user.maintenanceDetail.detail);
                    setTotalRecords(user.maintenanceDetail.totalRecordCount);
                    setMaintenanceActivityList(MaintenanceList);
                } else {
                    setMaintenanceActivityList([]);
                }
                setIsDataLoaded(false);
            } else if (!user.maintenanceDetailLoading && user.maintenanceDetailFail) {
                setMaintenanceActivityList([]);
                setIsDataLoaded(false);
            }
        }
        dispatch(UserManagementActions.maintenanceDetailUnmounted())

    }, [user.maintenanceDetailLoading, user.maintenanceDetailSuccess, user.maintenanceDetailFail]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        setOffset(currentPage + 1)
    }, [currentPage])

    return (
        <>
            <DataTable
                isLoading={isDataLoaded}
                columns={columns}
                records={maintenanceActivityList}
                count={totalRecords}
                currentPage={currentPage}
                emptyListMessage={t('MAINTENANCE_DETAILS.NO_MAINTENACE_FOUND')}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onSort={handleSort}
            />
        </>
    );
};

export default MaintenanceListTable;
